import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ErrorBox } from './common/ErrorBox';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { useFormContext } from 'react-hook-form';
import { InfoModal } from '../components/InfoModal';
import { IoChevronDown } from 'react-icons/io5';

const SelectPicker = ({
  name,
  label,
  title,
  errors,
  detailed_label,
  validation,
  options = [],
  defaultValue,
  description,
  placeholder = 'Selecteer een optie...',
  disabled = false
}) => {
  const { register, setValue, getValues, watch } = useFormContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Get current value from form or default
  const currentValue = watch(name) || defaultValue;

  // Find the selected option
  const selectedOption = options.find(opt => opt.value === currentValue);

  // Set initial value on mount
  React.useEffect(() => {
    const formValue = getValues(name);
    if ((formValue === undefined || formValue === '') && defaultValue) {
      setValue(name, defaultValue, { shouldValidate: false });
    }
  }, [name, defaultValue, setValue, getValues]);

  // Handle option selection
  const handleSelect = (option) => {
    setValue(name, option.value, { shouldValidate: true });
    setIsOpen(false);
  };

  return (
    <>
      <div className="bg-white/60 rounded-2xl shadow-sm border border-gray-200/60 p-3.5 transition-all duration-200 hover:bg-white/80 hover:shadow-md backdrop-blur-sm">
        <div className="flex justify-between items-start mb-2">
          <h2 className='text-base font-semibold text-gray-900'>{title}</h2>
          <AiOutlineInfoCircle
            className="text-gray-600 text-base cursor-pointer hover:text-gray-800 transition-colors flex-shrink-0"
            onClick={() => setIsModalOpen(true)}
          />
        </div>

        <p className="text-sm text-gray-600 mb-3">{detailed_label}</p>

        {/* Custom Select Dropdown */}
        <div className="relative">
          <button
            ref={buttonRef}
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={`w-full bg-white/80 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-gray-400 focus:border-gray-400 hover:border-gray-400 p-3 text-left transition-all duration-200 shadow-sm flex justify-between items-center ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            <div className="flex flex-col">
              {selectedOption ? (
                <>
                  <span className="font-medium">{selectedOption.label}</span>
                  {selectedOption.description && (
                    <span className="text-xs text-gray-500 mt-0.5">{selectedOption.description}</span>
                  )}
                </>
              ) : (
                <span className="text-gray-400">{placeholder}</span>
              )}
            </div>
            <IoChevronDown className={`text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Options - rendered via Portal */}
          {isOpen && createPortal(
            <>
              {/* Click outside to close */}
              <div
                className="fixed inset-0 z-[9998]"
                onClick={() => setIsOpen(false)}
              />
              <div
                className="fixed z-[9999] bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden max-h-60 overflow-y-auto"
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                  width: dropdownPosition.width
                }}
              >
                {options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`w-full p-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                      currentValue === option.value ? 'bg-gray-100' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex flex-col">
                        <span className="font-medium text-sm text-gray-900">{option.label}</span>
                        {option.description && (
                          <span className="text-xs text-gray-500 mt-0.5">{option.description}</span>
                        )}
                      </div>
                      {option.badge && (
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full ml-2">
                          {option.badge}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>,
            document.body
          )}
        </div>

        {/* Hidden input for form registration */}
        <input
          type="hidden"
          {...register(name, validation)}
          value={currentValue || ''}
        />

        <ErrorBox errors={errors} />
      </div>

      <InfoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={title}
      >
        <p className="text-sm leading-relaxed">{description}</p>
      </InfoModal>
    </>
  );
};

export default SelectPicker;
